import { ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { DataTableEmptyState } from './DataTableEmptyState';

export interface DataTableColumn<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /**
   * Emphasised on the mobile card (rendered before the muted columns). Kept for
   * source compatibility with existing call sites; on desktop every column shows.
   */
  priority: boolean;
  /** Align the cell's value to the end (right) in the desktop table. */
  alignEnd?: boolean;
}

interface DataTableProps<T> {
  columns: DataTableColumn<T>[];
  data: T[];
  getRowKey: (row: T) => string;
  emptyMessage?: string;
}

const labelOf = (header: ReactNode) => (typeof header === 'string' ? header : '');

// Mobile-first responsive table driven by CSS *container* queries (the table
// reacts to the width of its own wrapper — sidebar collapsed vs expanded — not
// the viewport). Below @md every row reflows into a labelled card that shows
// ALL columns (label via `data-label` + a `::before`), so nothing is lost on a
// phone; at @md and up it becomes a conventional, horizontally-scrollable table.
export function DataTable<T>({ columns, data, getRowKey, emptyMessage }: DataTableProps<T>) {
  if (data.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card shadow-sm">
        <DataTableEmptyState message={emptyMessage} />
      </div>
    );
  }

  return (
    <div className="@container w-full">
      <div className="@2xl:overflow-x-auto @2xl:rounded-xl @2xl:border @2xl:border-border @2xl:bg-card @2xl:shadow-sm">
        <table className="block w-full text-sm @2xl:table">
          <thead className="hidden @2xl:table-header-group">
            <tr className="border-b border-border">
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={cn(
                    'whitespace-nowrap bg-muted/50 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground',
                    column.alignEnd && 'text-right',
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="block @2xl:table-row-group">
            {data.map((row) => (
              <tr
                key={getRowKey(row)}
                className={cn(
                  'mb-3 block rounded-xl border border-border bg-card p-3 shadow-sm',
                  '@2xl:mb-0 @2xl:table-row @2xl:rounded-none @2xl:border-0 @2xl:border-b @2xl:border-border @2xl:bg-transparent @2xl:p-0 @2xl:shadow-none @2xl:transition-colors @2xl:last:border-0 @2xl:hover:bg-muted/40',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    data-label={labelOf(column.header)}
                    className={cn(
                      'flex items-center justify-between gap-4 border-b border-border/60 py-2 text-right last:border-0',
                      "before:mr-auto before:text-xs before:font-medium before:uppercase before:tracking-wide before:text-muted-foreground before:content-[attr(data-label)]",
                      '@2xl:table-cell @2xl:border-0 @2xl:px-3 @2xl:py-2.5 @2xl:text-left @2xl:align-middle @2xl:before:hidden @2xl:before:content-none',
                      column.alignEnd && '@2xl:text-right',
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
